import { legIssues, legIssueLabel } from "@/features/route/legAlerts.js"
import { transitLabel } from "@/features/route/transits.js"

const ms = (iso) => {
  const t = iso ? Date.parse(iso) : NaN
  return Number.isFinite(t) ? t : null
}

export const waypointTimes = (features) => features.map((f) => ms(f?.properties?.timestamp))

// Position of time t on the waypoint index axis: i + share of leg i; clamped to 0 … n-1.
export function fractionalIndex(t, times) {
  const n = times.length
  if (t == null || n === 0) return null
  const known = times.map((v, i) => [v, i]).filter(([v]) => v != null)
  if (known.length === 0) return null
  if (t <= known[0][0]) return known[0][1]
  for (let k = 0; k < known.length - 1; k++) {
    const [a, i] = known[k]
    const [b, j] = known[k + 1]
    if (t <= b) return b > a ? i + ((t - a) / (b - a)) * (j - i) : i
  }
  return known.at(-1)[1]
}

const legIndex = (features, legNumber) => {
  const i = features.findIndex((f) => f?.properties?.number === legNumber)
  return i < 0 ? null : i
}

// [start, end] index span of a transit; falls back to its legs when times are missing.
export function transitSpan(item, features, times) {
  const last = features.length - 1
  const at = (end, flag, edge) => {
    if (end?.[flag]) return edge
    const byTime = fractionalIndex(ms(end?.time), times)
    if (byTime != null) return byTime
    const leg = legIndex(features, end?.legNumber)
    return leg == null ? null : Math.min(leg + 0.5, last)
  }
  const start = at(item.entry, "atDeparture", 0)
  const end = at(item.exit, "atArrival", last)
  if (start == null || end == null) return null
  return [Math.max(0, Math.min(start, end)), Math.min(last, Math.max(start, end))]
}

const SEVERITY = { warning: 0, info: 1 }

// Issues per row: transits the waypoint lies in or whose entry/exit is on its outgoing leg,
// plus the leg warnings of that leg; red before orange.
export function rowIssues(features, items) {
  const spans = items.map((it) => ({ it, from: ms(it.entry.time), to: ms(it.exit.time) }))
  return features.map((f) => {
    const p = f?.properties ?? {}
    const t = ms(p.timestamp)
    const number = p.number
    const main = !p.extended_number
    const transits = spans
      .filter(({ it, from, to }) => {
        const inside = t != null && from != null && to != null && t >= from && t <= to
        const onLeg = main && (it.entry.legNumber === number || it.exit.legNumber === number)
        return inside || onLeg
      })
      .map(({ it }) => ({
        id: it.id,
        transitId: it.id,
        kind: it.kind,
        level: it.level === "info" ? "info" : "warning",
        text: transitLabel(it, { long: true }),
      }))
    const legs = legIssues(p).map((issue) => ({
      id: `leg-${issue.kind}`,
      kind: "leg",
      level: issue.level,
      text: legIssueLabel(issue),
    }))
    return [...transits, ...legs].sort((a, b) => SEVERITY[a.level] - SEVERITY[b.level])
  })
}

// Greedy lanes: a lane is free once the previous band (incl. its outside label) plus gap ends before the start.
export function assignLanes(bands, gap = 8) {
  const laneEnds = []
  const lanes = {}
  ;[...bands]
    .sort((a, b) => a.start - b.start)
    .forEach((band) => {
      let lane = laneEnds.findIndex((end) => end + gap <= band.start)
      if (lane < 0) lane = laneEnds.length
      laneEnds[lane] = band.end
      lanes[band.id] = lane
    })
  return { lanes, count: laneEnds.length }
}
